import { useState } from 'react';

/**
 * Charts are deliberately few and plain. Palette validated (light surface): blocked
 * #c2412d, sensitive-allowed #3a6db3, warned/justified #b07518 — stacked in that
 * fixed order so the red and amber never touch. Identity is never color-only: every
 * chart has a legend and a hover tooltip with the numbers, and a table toggle.
 */
export interface SeriesDef {
  key: string;
  label: string;
  color: string;
}

export function StackedDaily({ data, series, height = 170 }: { data: Array<Record<string, number | string>>; series: SeriesDef[]; height?: number }) {
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);
  const W = 720;
  const H = height;
  const padL = 34;
  const padB = 22;
  const totals = data.map((d) => series.reduce((s, x) => s + Number(d[x.key] ?? 0), 0));
  const max = Math.max(1, ...totals);
  const nice = niceMax(max);
  const bw = (W - padL) / Math.max(1, data.length);
  const barW = Math.max(3, Math.min(28, bw - 4));
  const y = (v: number) => (H - padB) * (1 - v / nice);
  const ticks = [0, nice / 2, nice];

  if (asTable) {
    return (
      <div>
        <Legend series={series} />
        <div className="table-wrap">
          <table className="t">
            <thead><tr><th>Day</th>{series.map((s) => <th key={s.key} className="num">{s.label}</th>)}</tr></thead>
            <tbody>{data.map((d, i) => <tr key={i}><td>{String(d.day)}</td>{series.map((s) => <td key={s.key} className="num">{Number(d[s.key] ?? 0)}</td>)}</tr>)}</tbody>
          </table>
        </div>
        <button className="btn ghost small" onClick={() => setAsTable(false)}>Show chart</button>
      </div>
    );
  }

  return (
    <div className="chart">
      <Legend series={series} />
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Daily ${series.map((s) => s.label).join(', ')}`} onMouseLeave={() => setHover(null)}>
        <g className="grid">{ticks.map((t) => <line key={t} x1={padL} x2={W} y1={y(t)} y2={y(t)} />)}</g>
        <g className="axis">
          {ticks.map((t) => <text key={t} x={padL - 6} y={y(t) + 3} textAnchor="end">{Math.round(t)}</text>)}
          {data.map((d, i) => (i % Math.ceil(data.length / 8) === 0 || i === data.length - 1) && (
            <text key={i} x={padL + i * bw + bw / 2} y={H - 6} textAnchor="middle">{shortDay(String(d.day))}</text>
          ))}
        </g>
        {data.map((d, i) => {
          let acc = 0;
          const x = padL + i * bw + (bw - barW) / 2;
          const segs = series.map((s, si) => {
            const v = Number(d[s.key] ?? 0);
            if (v <= 0) return null;
            const y0 = y(acc);
            acc += v;
            const y1 = y(acc);
            const isTop = series.slice(si + 1).every((n) => Number(d[n.key] ?? 0) <= 0);
            // 2px surface gap between stacked segments; rounded data-end only on the top segment.
            const h = Math.max(1, y0 - y1 - (isTop ? 0 : 2));
            return <rect key={s.key} x={x} y={y1 + (isTop ? 0 : 2)} width={barW} height={h} rx={isTop ? 3 : 0} fill={s.color} opacity={hover === null || hover === i ? 1 : 0.45} />;
          });
          return (
            <g key={i}>
              {segs}
              {/* Hit target wider than the mark */}
              <rect x={padL + i * bw} y={0} width={bw} height={H - padB} fill="transparent" onMouseEnter={() => setHover(i)} />
            </g>
          );
        })}
      </svg>
      {hover !== null && data[hover] && (
        <div className="tooltip" style={{ left: `${((padL + hover * bw + bw / 2) / W) * 100}%`, top: `${(y(totals[hover]!) / H) * 100}%` }}>
          <b>{String(data[hover]!.day)}</b>
          {series.slice().reverse().map((s) => (
            <div key={s.key}><i className="swatch" style={{ background: s.color }} />{s.label}: {Number(data[hover]![s.key] ?? 0)}</div>
          ))}
        </div>
      )}
      <button className="btn ghost small" onClick={() => setAsTable(true)}>View as table</button>
    </div>
  );
}

function Legend({ series }: { series: SeriesDef[] }) {
  return (
    <div className="legend">
      {series.map((s) => (
        <span key={s.key}><i style={{ background: s.color }} />{s.label}</span>
      ))}
    </div>
  );
}

/** Horizontal part-to-whole bars, one row per category, direct-labeled with the total. */
export function BarRows({ rows, series, labelWidth }: { rows: Array<{ label: React.ReactNode; values: number[]; total?: number }>; series: SeriesDef[]; labelWidth?: number }) {
  const max = Math.max(1, ...rows.map((r) => r.values.reduce((a, b) => a + b, 0)));
  return (
    <div>
      {series.length > 1 && <Legend series={series} />}
      {rows.map((r, i) => {
        const tot = r.total ?? r.values.reduce((a, b) => a + b, 0);
        return (
          <div className="bar-row" key={i} style={labelWidth ? { gridTemplateColumns: `${labelWidth}px 1fr 60px` } : undefined}>
            <div className="truncate">{r.label}</div>
            <div className="bar-track" title={series.map((s, k) => `${s.label}: ${r.values[k] ?? 0}`).join(' · ')}>
              {r.values.map((v, k) => v > 0 && <span key={k} style={{ width: `${(v / max) * 100}%`, background: series[k]!.color, borderRadius: k === r.values.length - 1 || r.values.slice(k + 1).every((x) => !x) ? '0 3px 3px 0' : 0 }} />)}
            </div>
            <div className="num">{tot.toLocaleString()}</div>
          </div>
        );
      })}
    </div>
  );
}

function niceMax(v: number) {
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}
function shortDay(d: string) {
  const dt = new Date(d + 'T00:00:00');
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
